import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Users, CheckCircle, Play, Film } from 'lucide-react';
import ClipPlayerModal from '../components/ClipPlayerModal';
import { sanitizeTwitchUsername, sanitizeImageUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const GENRE_CHIPS = [
  { label: 'Horror',       q: 'horror' },
  { label: 'FPS',          q: 'fps' },
  { label: 'Battle Royale',q: 'battle royale' },
  { label: 'RPG',          q: 'rpg' },
  { label: 'Sports',       q: 'sports' },
  { label: 'Fighting',     q: 'fighting' },
  { label: 'Racing',       q: 'racing' },
  { label: 'Strategy',     q: 'strategy' },
  { label: 'Sandbox',      q: 'sandbox' },
  { label: 'Anime',        q: 'anime' },
  { label: 'Indie',        q: 'indie' },
  { label: 'Simulation',   q: 'simulation' },
  { label: 'Just Chatting',q: 'just chatting' },
  { label: 'Competitive',  q: 'competitive' },
  { label: 'Retro',        q: 'retro' },
  { label: 'Minecraft',    q: 'minecraft' },
  { label: 'Fortnite',     q: 'fortnite' },
  { label: 'Pokémon',      q: 'pokemon' },
];

const StreamerCard = ({ streamer }) => {
  const safeLogin  = sanitizeTwitchUsername(streamer.login);
  const safeAvatar = sanitizeImageUrl(streamer.profile_image_url);
  if (!safeLogin) return null;
  return (
  <Link
    to={`/profile/${safeLogin}`}
    className="flex items-center gap-4 p-4 bg-white border border-gray-200 hover:border-black transition-colors duration-200 group"
  >
    <img
      src={safeAvatar ?? '/images/default-avatar.png'}
      alt={streamer.display_name}
      className="w-14 h-14 rounded-full object-cover flex-shrink-0 border border-gray-100"
      onError={e => { e.target.src = '/images/default-avatar.png'; }}
    />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="font-semibold text-black text-sm truncate group-hover:underline">
          {streamer.display_name}
        </p>
        {streamer.is_verified && (
          <CheckCircle size={14} className="text-blue-500 flex-shrink-0" />
        )}
      </div>
      <p className="text-gray-500 text-xs truncate mt-0.5">@{streamer.login}</p>
      {streamer.game_tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {streamer.game_tags.slice(0, 3).map(tag => (
            <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  </Link>
  );
};

// Clip card for search results
const ClipCard = ({ clip, onPlay }) => {
  const [hovered, setHovered] = useState(false);
  const thumb = sanitizeImageUrl(clip.thumbnail_url);
  const views = clip.view_count >= 1000
    ? (clip.view_count / 1000).toFixed(1) + 'K'
    : String(clip.view_count || 0);

  return (
    <button
      onClick={() => onPlay(clip)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="text-left w-full group"
    >
      <div className="relative aspect-video bg-gray-900 overflow-hidden mb-2">
        {thumb && (
          <img
            src={thumb}
            alt={clip.title}
            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
          />
        )}
        {/* Play overlay */}
        <div className={`absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity duration-200 ${hovered ? 'opacity-100' : 'opacity-0'}`}>
          <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center">
            <Play size={20} color="#000" fill="#000" style={{ marginLeft: 2 }} />
          </div>
        </div>
        {/* Duration */}
        {clip.duration && (
          <div className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-xs px-1.5 py-0.5 font-mono">
            {Math.floor(clip.duration / 60)}:{String(Math.round(clip.duration % 60)).padStart(2, '0')}
          </div>
        )}
      </div>
      <p className="text-black text-sm font-medium leading-snug line-clamp-2 mb-1">{clip.title}</p>
      <div className="flex items-center gap-2 text-xs text-gray-400">
        {clip.broadcaster_name && <span>{clip.broadcaster_name}</span>}
        {clip.broadcaster_name && clip.game_name && <span>·</span>}
        {clip.game_name && <span className="truncate">{clip.game_name}</span>}
        <span className="ml-auto flex-shrink-0">{views} views</span>
      </div>
    </button>
  );
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [input,      setInput]      = useState(searchParams.get('q') || '');
  const [query,      setQuery]      = useState(searchParams.get('q') || '');
  const [results,    setResults]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [meta,       setMeta]       = useState(null);
  const [searched,   setSearched]   = useState(false);
  const [clips,      setClips]      = useState([]);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [activeClip, setActiveClip] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!query) {
      setResults([]); setMeta(null); setSearched(false); setClips([]);
      return;
    }
    setLoading(true);
    setClipsLoading(true);
    setSearched(true);

    const params = new URLSearchParams({ q: query, limit: 30 });

    // Fetch streamers and clips in parallel
    Promise.all([
      fetch(`${BACKEND_URL}/api/search/streamers?${params}`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/search/clips?${new URLSearchParams({ q: query, limit: 12 })}`).then(r => r.json()),
    ])
      .then(([streamerData, clipData]) => {
        setResults(streamerData.streamers || []);
        setMeta({ genre: streamerData.genre, games: streamerData.games || [] });
        setClips(clipData.clips || []);
      })
      .catch(() => { setResults([]); setClips([]); })
      .finally(() => { setLoading(false); setClipsLoading(false); });
  }, [query]);

  const handleSubmit = e => {
    e.preventDefault();
    const q = input.trim();
    setQuery(q);
    if (q) setSearchParams({ q });
    else setSearchParams({});
  };

  const handleChip = q => {
    setInput(q);
    setQuery(q);
    setSearchParams({ q });
  };

  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <section className="bg-black py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-medium uppercase tracking-widest text-gray-500 mb-3">BGN</p>
          <h1 className="text-5xl lg:text-6xl font-black text-white uppercase leading-none mb-8">
            Find Streamers
          </h1>

          <form onSubmit={handleSubmit} className="relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder='Try "horror", "fps", "rpg", "sports"...'
              className="w-full bg-white text-black text-lg pl-12 pr-28 py-4 outline-none border-0"
            />
            <button
              type="submit"
              className="absolute right-0 top-0 h-full bg-black text-white px-6 text-sm font-medium uppercase tracking-wide hover:bg-gray-800 transition-colors border-l border-gray-700"
            >
              Search
            </button>
          </form>

          {/* Genre chips */}
          <div className="flex flex-wrap gap-2 mt-6">
            {GENRE_CHIPS.map(chip => (
              <button
                key={chip.q}
                onClick={() => handleChip(chip.q)}
                className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide border transition-colors duration-150 ${
                  query === chip.q
                    ? 'bg-white text-black border-white'
                    : 'bg-transparent text-gray-400 border-gray-700 hover:border-gray-400 hover:text-white'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-5xl mx-auto px-4 py-12">

        {/* Meta heading */}
        {meta?.genre && (
          <div className="mb-8">
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">
              Showing streamers who play
            </p>
            <h2 className="text-3xl font-black text-black uppercase">{meta.genre}</h2>
            {meta.games?.length > 0 && (
              <p className="text-xs text-gray-400 mt-1">
                Matching: {meta.games.join(', ')}
              </p>
            )}
          </div>
        )}

        {meta && !meta.genre && query && (
          <div className="mb-8">
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">Results for</p>
            <h2 className="text-3xl font-black text-black uppercase">"{query}"</h2>
          </div>
        )}

        {/* Two-column layout once there's a query */}
        {searched ? (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* LEFT — Streamers */}
            <div className="lg:col-span-2">
              <p className="text-xs uppercase tracking-widest text-gray-400 font-medium mb-4 border-b border-gray-100 pb-2">
                BGN Streamers
              </p>

              {loading && (
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 border border-gray-100">
                      <div className="w-14 h-14 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 bg-gray-200 animate-pulse" />
                        <div className="h-3 w-20 bg-gray-100 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && results.length > 0 && (
                <>
                  <p className="text-xs text-gray-400 mb-3">
                    {results.length} streamer{results.length !== 1 ? 's' : ''} found
                  </p>
                  <div className="space-y-2">
                    {results.map(s => <StreamerCard key={s.twitch_id} streamer={s} />)}
                  </div>
                </>
              )}

              {!loading && results.length === 0 && (
                <div className="text-center py-12">
                  <Users size={32} className="text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No BGN streamers found</p>
                </div>
              )}
            </div>

            {/* RIGHT — Clips */}
            <div className="lg:col-span-3">
              <p className="text-xs uppercase tracking-widest text-gray-400 font-medium mb-4 border-b border-gray-100 pb-2">
                Top Clips
              </p>

              {clipsLoading && (
                <div className="grid grid-cols-2 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i}>
                      <div className="aspect-video bg-gray-200 animate-pulse mb-2" />
                      <div className="h-3 w-3/4 bg-gray-200 animate-pulse mb-1" />
                      <div className="h-3 w-1/2 bg-gray-100 animate-pulse" />
                    </div>
                  ))}
                </div>
              )}

              {!clipsLoading && clips.length > 0 && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-8">
                  {clips.map(clip => (
                    <ClipCard key={clip.id} clip={clip} onPlay={setActiveClip} />
                  ))}
                </div>
              )}

              {!clipsLoading && clips.length === 0 && (
                <div className="text-center py-12">
                  <Film size={32} className="text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No clips found for this genre</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Empty state — no search yet */
          <div className="text-center py-16">
            <Search size={40} className="text-gray-200 mx-auto mb-4" />
            <p className="text-gray-500">Search by genre or streamer name</p>
          </div>
        )}

      </section>

      {activeClip && <ClipPlayerModal clip={activeClip} onClose={() => setActiveClip(null)} />}
    </div>
  );
};

export default SearchPage;
